/**
 * Route guard for the missed-appointment recovery queue
 * (`/dashboard/coc/no-shows`) -- FRONT-DESK-DAY-BOARD-V1. Mirrors
 * RequireFollowupQueue: the pure decision lives in decideNoShowQueueAccess
 * (no-show-queue-access.ts) and keys off the server-computed
 * `missedAppointmentQueueEnabled` /me door signal (the org's Q-05=D
 * audience config decides WHO reaches the queue -- e.g. a front desk the
 * org granted with one Studio row) with the 'coc' nav section preserved as
 * the fail-same legacy arm.
 *
 * This REPLACES RequireNavSection section="coc" for THIS ONE route so a
 * config-admitted role (a granted receptionist) reaches the queue WITHOUT
 * inheriting the COC nav menu (the RequireFollowupQueue decoupling
 * precedent). Every 'coc'-section caller renders exactly as before.
 * Backend route auth remains the real security boundary.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideNoShowQueueAccess } from '@/dashboard/coc/no-show-queue-access';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireNoShowQueueViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireNoShowQueueView({
  loading,
  error,
  me,
  children,
}: RequireNoShowQueueViewProps): JSX.Element | null {
  const decision = decideNoShowQueueAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireNoShowQueue(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireNoShowQueueView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}
